import * as React from 'react';
import {
    Box,
    Typography,
    Stack,
    useTheme
} from '@mui/material';
import HtmlContent from '../../util/HtmlContent';

const MAX_VISIBLE_EVENTS = 3;

export default function MesCard(props) {
    const { data, dataCorrent, eventos, feriado, setData, setCalendarType, setIdEvento,
        setShowEvento} = props;
    const theme = useTheme();

    const isCurrentMonth = data.getMonth() === dataCorrent.getMonth();
    const isToday = React.useMemo(() => {
        const hoje = new Date();
        return data.getDate() === hoje.getDate() &&
            data.getMonth() === hoje.getMonth() &&
            data.getFullYear() === hoje.getFullYear();
    }, [data]);

    const visivelFeriado = React.useMemo(() => {
        if (!feriado) return [];
        return feriado.lst.filter(e => {
            const [ano, mes, dia] = e.date.split('-');
            const dtInicial = new Date(ano, mes - 1, dia);
            return data.getDate() === dtInicial.getDate() &&
                data.getMonth() === dtInicial.getMonth();
        });
    }, [data, feriado]);

    const eventosDoDia = React.useMemo(() => {
        const arr = eventos.filter(e => {
            const dtInicial = new Date(e.dtInicial);
            dtInicial.setHours(0, 0, 0, 0);

            let dtFinal = new Date(e.dtInicial);
            if (e.dtFinal) dtFinal = new Date(e.dtFinal);
            dtFinal.setHours(23, 59, 59, 0);

            return (data >= dtInicial && data <= dtFinal);
        });
        return arr.sort((a, b) => new Date(a.dtInicial) - new Date(b.dtInicial));
    }, [data, eventos]);

    const hasPrincipal = eventosDoDia.some(e => e.isPrincipal);
    const eventosParaMostrar = eventosDoDia.slice(0, MAX_VISIBLE_EVENTS);
    const eventosOcultos = eventosDoDia.length - MAX_VISIBLE_EVENTS;

    const handleIrParaDia = (e) => {
        e.stopPropagation();
        setData(data);
        setCalendarType('diario');
    };

    const handleAbrirEventoClicado = () => {
            const eventoParaAbrir = eventosDoDia.find(e => e.isPrincipal) || eventosDoDia[0];
            setIdEvento(eventoParaAbrir.id);
            setShowEvento(true);
    };

    function isEventoLongo(row) {
        if (!row.dtFinal) return false;
        const dtIni = new Date(row.dtInicial);
        const dtFim = new Date(row.dtFinal);
        return dtFim.getDate() !== dtIni.getDate() || dtFim.getMonth() !== dtIni.getMonth();
    }

    function getCorFundo() {
        if (visivelFeriado.length > 0) return theme.palette.error.light + '1A';
        return isCurrentMonth ? 'transparent' : theme.palette.action.hover;
    }

    return (
        <Box
            onClick={handleAbrirEventoClicado}
            sx={{
                width: '100%',
                height: '100%',
                minHeight: '120px',
                bgcolor: getCorFundo(),
                p: 1,
                display: 'flex',
                flexDirection: 'column',
                opacity: isCurrentMonth ? 1 : 0.6,
                cursor: 'pointer',
                transition: 'background-color 0.2s',
                '&:hover': {
                    bgcolor: isCurrentMonth ? theme.palette.action.hover : getCorFundo()
                }
            }}
        >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                <Typography
                    variant="caption"
                    sx={{
                        color: theme.palette.error.main,
                        fontWeight: 500,
                        maxWidth: '70%',
                        lineHeight: 1.1,
                        fontSize: '0.65rem'
                    }}
                >
                    {visivelFeriado.length > 0 && (visivelFeriado.length === 1 ? visivelFeriado[0].name : "Feriado")}
                </Typography>

                <Box
                    onClick={handleIrParaDia}
                    sx={{
                        width: 24,
                        height: 24,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '50%',
                        bgcolor: isToday ? theme.palette.primary.main : 'transparent',
                        color: isToday ? theme.palette.primary.contrastText : 'text.primary',
                        fontWeight: isToday ? 'bold' : 'normal',
                        fontSize: '0.875rem',
                        zIndex:2,
                        '&:hover': { bgcolor: isToday ? theme.palette.primary.dark : theme.palette.action.selected }
                    }}
                >
                    {data.getDate()}
                </Box>
            </Box>

            <Box sx={{ flex: 1, overflow: 'hidden' }}>
                {hasPrincipal ? (
                    <Box
                        sx={{
                            position: 'relative',
                            maxHeight: '80px',
                            overflow: 'hidden',
                            maskImage: 'linear-gradient(to bottom, black 50%, transparent 100%)',
                            WebkitMaskImage: 'linear-gradient(to bottom, black 50%, transparent 100%)',
                            '& *': {
                                fontSize: '0.7rem !important',
                                m: 0,
                                p: 0,
                                lineHeight: '1.2 !important'
                            }
                        }}
                    >
                        {/* <HtmlContent htmlString={eventosDoDia.find(ev => ev.isPrincipal).pagina} /> */}
                        <a href={eventosDoDia.find(ev => ev.isPrincipal).pagina}> {eventosDoDia.find(ev => ev.isPrincipal).nomeCurto} </a>
                    </Box>
                ) : (
                    <Stack spacing={0.5}>
                        {eventosParaMostrar.map((row) => (
                            <Box
                                key={row.id}
                                sx={{
                                    bgcolor: isEventoLongo(row) ? theme.palette.primary.light : 'transparent',
                                    color: isEventoLongo(row) ? theme.palette.primary.contrastText : theme.palette.text.primary,
                                    borderRadius: 1,
                                    px: 0.5,
                                    py: 0.2,
                                    fontSize: '0.7rem',
                                    fontWeight: isEventoLongo(row) ? 600 : 400,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                }}
                            >
                                {!isEventoLongo(row) && (
                                    <Box component="span" sx={{ color: theme.palette.primary.main, mr: 0.5, fontWeight: 'bold' }}>•</Box>
                                )}
                                {row.nomeCurto}
                            </Box>
                        ))}
                    </Stack>
                )}

                {eventosOcultos > 0 && !hasPrincipal && (
                    <Typography
                        variant="caption"
                        sx={{
                            color: theme.palette.text.secondary,
                            display: 'block',
                            mt: 0.5,
                            fontWeight: 500
                        }}
                    >
                        + {eventosOcultos} mais
                    </Typography>
                )}
            </Box>

        </Box>
    );
}